/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { NotEmptyValidator } from '@squiz/dxp-console-support';
import {
  AVATAR_SIZE_LARGE,
  Avatar,
  BUTTON_FORMAT_SOFT,
  BUTTON_THEME_DEFAULT,
  Button,
  DialogContentProps,
  FORM_THEME_LITE,
  Form,
  TOAST_TYPE_SUCCESS,
  TextField,
  TextFieldProps,
  Toast,
  ToastProps,
} from '@squiz/sds';
import { useConsoleServiceStore, useToastStore } from 'dxpConsoleCore-mf/State';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { lastValueFrom } from 'rxjs';
import { CORE_STRINGS } from '../../Constants/Strings.constants';
import { ConsoleServiceUpdateUserDto, EntityDxpUserMe, getConsoleService } from '../../Services/console';
import { MY_ACCOUNT_DATA_IDS, MY_ACCOUNT_FORM_IDS } from './MyAccountForm.constants';

/**
 * The My Account form.
 */
export const MyAccountForm = (props: DialogContentProps): JSX.Element => {
  const { t } = useTranslation();
  const { close, setButtonKeys, setButtons } = props;

  // Console service
  const consoleService = getConsoleService();
  const { consoleUser }: { consoleUser: EntityDxpUserMe } = useConsoleServiceStore();
  const { updateConsoleUser } = useConsoleServiceStore();

  const userProfile = useMemo(() => {
    return {
      email: consoleUser.email,
      firstName: consoleUser.givenName,
      lastName: consoleUser.familyName,
    };
  }, [consoleUser]);

  const { updateActiveToast } = useToastStore();

  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});
  const [formError, setFormError] = useState<string>(``);
  const [isFormSubmitted, setSubmit] = useState<boolean>(false);
  const [isBusy, setIsBusy] = useState<boolean>(false);

  const [firstNameInputValue, setFirstNameInputValue] = useState<string>(userProfile.firstName);
  const [lastNameInputValue, setLastNameInputValue] = useState<string>(userProfile.lastName);
  const [buttonsDisabled, setButtonsDisabled] = useState<boolean>(true);

  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  const notEmptyValidator = useMemo(() => {
    return new NotEmptyValidator<boolean>((): boolean => false);
  }, []);

  // Labels
  const labels = useMemo(() => {
    return {
      closeButton: t(CORE_STRINGS.close),
      dialogTitle: t(CORE_STRINGS.myAccount),
      email: t(CORE_STRINGS.email),
      errorMessage: t(CORE_STRINGS.myAccountError),
      firstName: t(CORE_STRINGS.firstName),
      lastName: t(CORE_STRINGS.lastName),
      requiredField: t(CORE_STRINGS.requiredField),
      resetButton: t(CORE_STRINGS.reset),
      saveButton: t(CORE_STRINGS.save),
      successMessage: t(CORE_STRINGS.myAccountSuccess),
    };
  }, [t]);

  const handleFormReset = useCallback((): void => {
    const inputs = {
      firstName: firstNameRef.current as HTMLInputElement,
      lastName: lastNameRef.current as HTMLInputElement,
    };
    inputs.firstName.value = userProfile.firstName;
    inputs.lastName.value = userProfile.lastName;
  }, [userProfile]);

  const validateFields = useCallback(
    (inputs: Array<HTMLInputElement>): void => {
      const nextErrors = { ...fieldErrors };

      inputs.forEach((input: HTMLInputElement): void => {
        const inputValue = input.value;
        const inputFieldId = input.getAttribute(`id`) as string;

        if (!notEmptyValidator.validate(String(inputValue))) {
          nextErrors[inputFieldId] = labels.requiredField;
        } else {
          delete nextErrors[inputFieldId];
        }
      });

      setFieldErrors(nextErrors);
    },
    [fieldErrors, labels, notEmptyValidator, setFieldErrors],
  );

  const errorChecker = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      validateFields([event.target]);
    },
    [validateFields],
  );

  const submitHandler = useCallback(() => {
    setIsBusy(true);

    // Set up inputs
    const inputs = {
      firstName: firstNameRef.current as HTMLInputElement,
      lastName: lastNameRef.current as HTMLInputElement,
    };

    // Collect the current values of the form
    const newFirstName = inputs.firstName.value;
    const newLastName = inputs.lastName.value;

    // Validate the required input fields
    if (
      !notEmptyValidator.validate(String(inputs.firstName.value)) ||
      !notEmptyValidator.validate(String(inputs.lastName.value))
    ) {
      const inputsToCheck = [inputs.firstName, inputs.lastName];
      validateFields(inputsToCheck);
      setSubmit(false);
      setIsBusy(false);
      setFormError(labels.errorMessage);
      return;
    }

    // Clear validation errors if present
    setFormError(``);

    // Prepare the DTO
    const dto: ConsoleServiceUpdateUserDto = {
      familyName: newLastName,
      givenName: newFirstName,
      userId: consoleUser.referenceId,
    };

    // Success toast props
    const toastProps: ToastProps = {
      showToast: true,
      stateHandler: useToastStore,
      toastContent: <strong>{labels.successMessage}</strong>,
      toastType: TOAST_TYPE_SUCCESS,
    };

    const updateConsoleServiceWithUser = (): void => {
      lastValueFrom(consoleService.updateUser(dto))
        .then((consoleResponse) => {
          // Assign values from EntityDxpUser to current EntityDxpUserMe
          consoleUser.familyName = consoleResponse.familyName;
          consoleUser.givenName = consoleResponse.givenName;

          updateConsoleUser(consoleUser);
          updateActiveToast(<Toast {...toastProps} data-id={MY_ACCOUNT_DATA_IDS.successToast} />);
          close();
          return true;
        })
        .catch(() => {
          setFormError(labels.errorMessage);
          setSubmit(false);
          setIsBusy(false);
        });
    };

    // Update user profile
    updateConsoleServiceWithUser();
  }, [
    close,
    consoleService,
    consoleUser,
    labels,
    notEmptyValidator,
    updateActiveToast,
    updateConsoleUser,
    validateFields,
  ]);

  useEffect(() => {
    const inputs = {
      firstName: firstNameRef.current as HTMLInputElement,
      lastName: lastNameRef.current as HTMLInputElement,
    };
    setButtonsDisabled(
      inputs.firstName.value === userProfile.firstName && inputs.lastName.value === userProfile.lastName,
    );
  }, [firstNameInputValue, lastNameInputValue, userProfile.firstName, userProfile.lastName]);

  useEffect(() => {
    const resetButton = (
      <Button
        data-id={MY_ACCOUNT_DATA_IDS.resetButton}
        disabled={buttonsDisabled}
        format={BUTTON_FORMAT_SOFT}
        onClick={handleFormReset}
        text={labels.resetButton}
        theme={BUTTON_THEME_DEFAULT}
      />
    );
    const saveButton = (
      <Button
        data-id={MY_ACCOUNT_DATA_IDS.saveButton}
        disabled={buttonsDisabled}
        isLoading={isFormSubmitted}
        onClick={(): void => setSubmit(true)}
        text={labels.saveButton}
      />
    );

    // Form submission check
    if (isFormSubmitted) {
      setButtonKeys([`save`]);
      setButtons([saveButton]);

      if (!isBusy) {
        submitHandler();
      }
    } else {
      setButtonKeys([`reset`, `save`]);
      setButtons([resetButton, saveButton]);
    }
  }, [
    buttonsDisabled,
    handleFormReset,
    isBusy,
    isFormSubmitted,
    labels,
    setButtonKeys,
    setButtons,
    setSubmit,
    submitHandler,
  ]);

  // First name
  const firstNameFieldProps: TextFieldProps = {
    errorMessage: fieldErrors[MY_ACCOUNT_FORM_IDS.firstName] || null,
    fieldLabel: labels.firstName,
    id: MY_ACCOUNT_FORM_IDS.firstName,
    name: MY_ACCOUNT_FORM_IDS.firstName,
    onChange: (event: ChangeEvent<HTMLInputElement>): void => {
      errorChecker(event);
      setFirstNameInputValue(event.target.value);
    },
    value: userProfile.firstName,
  };

  // Last name
  const lastNameFieldProps: TextFieldProps = {
    errorMessage: fieldErrors[MY_ACCOUNT_FORM_IDS.lastName] || null,
    fieldLabel: labels.lastName,
    id: MY_ACCOUNT_FORM_IDS.lastName,
    name: MY_ACCOUNT_FORM_IDS.lastName,
    onChange: (event: ChangeEvent<HTMLInputElement>): void => {
      errorChecker(event);
      setLastNameInputValue(event.target.value);
    },
    value: userProfile.lastName,
  };

  // Email
  const emailFieldProps: TextFieldProps = {
    disabled: true,
    fieldLabel: labels.email,
    id: MY_ACCOUNT_FORM_IDS.email,
    name: MY_ACCOUNT_FORM_IDS.email,
    value: userProfile.email,
  };

  return (
    <div className="my-account__form">
      <div className="my-account__avatar">
        <Avatar avatarSize={AVATAR_SIZE_LARGE} user={userProfile} />
      </div>
      <Form errorMessage={formError} legend={labels.dialogTitle} theme={FORM_THEME_LITE}>
        <TextField {...firstNameFieldProps} data-id={MY_ACCOUNT_DATA_IDS.firstName} ref={firstNameRef} />
        <TextField {...lastNameFieldProps} data-id={MY_ACCOUNT_DATA_IDS.lastName} ref={lastNameRef} />
        <TextField {...emailFieldProps} ref={emailRef} />
      </Form>
    </div>
  );
};
